import { Component, ReactNode } from "react";
import { currentLanguage } from "./languageSegment";

interface Props {
  pathname: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorPathname: string | null;
}

// Without a boundary React 18 unmounts the WHOLE root on one screen's crash:
// the sidebar dies with it and every later navigation is silently dead.
export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorPathname: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: Props,
    state: State
  ): Partial<State> | null {
    // Navigating away from the crashed page recovers the app.
    if (
      state.error &&
      state.errorPathname &&
      props.pathname !== state.errorPathname
    ) {
      return { error: null, errorPathname: null };
    }
    return null;
  }

  componentDidCatch(error: Error) {
    console.error("Screen crashed:", error);
    this.setState({ errorPathname: this.props.pathname });
  }

  render() {
    if (this.state.error) {
      const language = window.languageContext?.language ?? {};
      const t = (key: string) => language[key] ?? key;
      const lang = currentLanguage(this.props.pathname);
      return (
        <div className="flex min-h-screen w-full items-center justify-center bg-[#F5F9F9] px-4 font-inter">
          <div className="w-full max-w-md rounded-2xl border border-[#DBE4E4] bg-white p-8 text-center shadow-lg">
            <h1 className="text-xl font-semibold text-[#222525]">
              {t("This page hit an error")}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-[#4A5353]">
              {t(
                "The rest of the back office is fine. You can go back to the dashboard or reload this page."
              )}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <a
                href={`/${lang}/dashboard`}
                className="inline-flex h-10 items-center justify-center rounded-[30px] bg-[#0F7679] px-5 text-sm font-medium text-white hover:bg-[#0D5759]"
              >
                {t("Go to Dashboard")}
              </a>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex h-10 items-center justify-center rounded-[30px] border border-[#DBE4E4] bg-white px-5 text-sm font-medium text-[#0D5759] hover:bg-[#EBF0F0]"
              >
                {t("Reload page")}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
